import type {
  Approval,
  ProviderRouteDiagnostic,
  RunExecutionState,
  StructuredMessagePayload,
  ToolCall,
  ToolResult,
  Usage,
} from './types.js';

export const AI_CHAT_AMBIGUITY_CANDIDATE_LIMIT = 5;

export const AI_CHAT_EVENT_SCHEMA_VERSION = 1 as const;
export const AI_CHAT_EVENT_MAX_PAYLOAD_BYTES = 64 * 1024;

export const AI_CHAT_EVENT_TYPES = [
  'assistant_delta',
  'reasoning_delta',
  'tool_call',
  'tool_result',
  'approval_required',
  'usage',
  'structured_payload',
  'client_action',
  'turn_status',
  'done',
  'error',
] as const;

export type AiChatEventType = (typeof AI_CHAT_EVENT_TYPES)[number];

// Legacy terminal names stay unchanged during the additive v1 rollout. The
// status-aware helper below handles terminal `turn_status` envelopes without
// making a streaming status look terminal to existing type-only consumers.
export const AI_CHAT_TERMINAL_EVENT_TYPES = [
  'done',
  'error',
] as const satisfies readonly AiChatEventType[];

export type AiChatMessageSnapshot = {
  id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  structuredPayload?: StructuredMessagePayload | null;
  toolCalls?: ToolCall[] | null;
  toolCallId?: string | null;
  createdAt?: string | Date;
};

export const AI_CHAT_TURN_STATUSES = [
  'streaming',
  'resumed',
  'completed',
  'failed',
  'cancelled',
] as const;

export type AiChatTurnStatus = (typeof AI_CHAT_TURN_STATUSES)[number];

export type AiChatEventCursor = {
  eventId: string;
  runId: string;
  turnId: string;
  sequence: number;
};

export type AiChatPublicTurnError = {
  message: string;
  code?: string;
  retryable?: boolean;
};

/**
 * Stable, user-safe explanation of why a turn stopped.  It is deliberately
 * additive: older event consumers can keep rendering the assistant/error text
 * while newer clients can present the precise outcome without parsing prose.
 *
 * `candidateHints` contains display labels only.  Database identifiers,
 * provider diagnostics and internal exception details never cross this
 * boundary.
 */
export const AI_CHAT_TERMINAL_OUTCOME_CODES = [
  'missing_field',
  'ambiguous_entity',
  'unsupported_operation',
  'normalization_failed',
  'system_error',
] as const;

export type AiChatTerminalOutcomeCode = (typeof AI_CHAT_TERMINAL_OUTCOME_CODES)[number];

export type AiChatTerminalCandidateHint = {
  label: string;
  entityType?: string;
};

export type AiChatTerminalOutcome = {
  code: AiChatTerminalOutcomeCode;
  field?: string;
  candidateHints?: AiChatTerminalCandidateHint[];
};

/**
 * Client actions are the only way a turn can ask the browser to move or to
 * re-show a list it already owns. They are navigation-only by construction: the
 * union below has no `url`/`href` field, so an external target is not expressible
 * at all, and every route, view and filter value is drawn from a closed
 * vocabulary. Anything the assistant wants to *write* still goes through a
 * server-owned, approval-gated tool call.
 *
 * `set_filter` and `switch_view` are pinned to a single route each. Encoding the
 * pairing in the type instead of validating it later means the contract cannot
 * describe a filter for a surface that does not own it, and the browser's own
 * allowlist cannot drift away from the vocabulary without a compile error.
 */
export const AI_CHAT_CLIENT_ACTION_TYPES = [
  'open_entity',
  'navigate',
  'set_filter',
  'switch_view',
] as const;

export type AiChatClientActionType = (typeof AI_CHAT_CLIENT_ACTION_TYPES)[number];

/** In-app sections a client action may address. Mirrors the router's list routes. */
export const AI_CHAT_CLIENT_ACTION_ROUTES = [
  'deals',
  'contacts',
  'tasks',
  'meetings',
  'projects',
  'documents',
  'companies',
  'templates',
] as const;

export type AiChatClientActionRoute = (typeof AI_CHAT_CLIENT_ACTION_ROUTES)[number];

/**
 * Entity identifiers are opaque to the client and must be resolved and scoped
 * by the server before they reach this boundary. The pattern rejects anything
 * that could escape the route segment (`/`, `\`, `.`, `%`), so a stored id can
 * never become a different path.
 */
export const AI_CHAT_CLIENT_ENTITY_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,199}$/;

/**
 * Views the Deals workbench exposes. Closed on purpose: `EntityTabs`-style tab
 * vocabularies are declared inline per card page and are deliberately *not*
 * reachable from here, because a client allowlist duplicating them would drift
 * and turn into a free-tab surface.
 */
export const AI_CHAT_CLIENT_VIEW_VALUES = ['kanban', 'list', 'cards'] as const;

export type AiChatClientView = (typeof AI_CHAT_CLIENT_VIEW_VALUES)[number];

/**
 * The only filter vocabulary a client action may name. Every value here is
 * finite and server-owned. Free-text search (`q`), user-defined tags and
 * relation ids are deliberately absent: their values are user data, and an
 * action is not a place to carry them.
 */
export const AI_CHAT_CLIENT_FILTER_KEYS = ['has'] as const;

export type AiChatClientFilterKey = (typeof AI_CHAT_CLIENT_FILTER_KEYS)[number];

export const AI_CHAT_CLIENT_FILTER_VALUES = ['deals', 'email'] as const;

export type AiChatClientFilterValue = (typeof AI_CHAT_CLIENT_FILTER_VALUES)[number];

export interface AiChatClientFilterSpec {
  readonly key: AiChatClientFilterKey;
  readonly value: AiChatClientFilterValue;
}

export type AiChatClientAction =
  | {
      readonly action: 'open_entity';
      readonly route: AiChatClientActionRoute;
      readonly entityId: string;
    }
  | {
      readonly action: 'navigate';
      readonly route: AiChatClientActionRoute;
    }
  | {
      /** Contacts is the only list surface whose filter state is a real URL parameter. */
      readonly action: 'set_filter';
      readonly route: 'contacts';
      readonly filter: AiChatClientFilterSpec;
    }
  | {
      /** Deals is the only list surface whose view toggle is a real URL parameter. */
      readonly action: 'switch_view';
      readonly route: 'deals';
      readonly view: AiChatClientView;
    };

export interface AiChatEventPayloadByType {
  assistant_delta: {
    content: string;
  };
  reasoning_delta: {
    content: string;
  };
  tool_call: {
    call: ToolCall;
  };
  tool_result: {
    result: ToolResult;
  };
  approval_required: {
    approval: Approval;
  };
  usage: {
    usage: Usage;
  };
  structured_payload: {
    messageId: string;
    payload: StructuredMessagePayload;
  };
  client_action: {
    action: AiChatClientAction;
  };
  turn_status: {
    status: AiChatTurnStatus;
    cursor: AiChatEventCursor;
    /**
     * Always present for `failed`. Also allowed for `cancelled` when the turn
     * was stopped by something other than the user (a deadline expiry), so the
     * client can tell a timeout apart from a user-initiated cancel. Any other
     * status must not carry an error.
     */
    error?: AiChatPublicTurnError;
  };
  done: {
    state: Extract<RunExecutionState, 'completed'>;
    messages?: AiChatMessageSnapshot[];
    usage?: Usage;
    traceId?: string;
    routeDiagnostic?: ProviderRouteDiagnostic;
    terminalOutcome?: AiChatTerminalOutcome;
  };
  error: {
    state: Extract<RunExecutionState, 'failed'>;
    error: {
      message: string;
      code?: string;
      terminalOutcome?: AiChatTerminalOutcome;
    };
  };
}

export type AiChatEventPayload<TType extends AiChatEventType> = AiChatEventPayloadByType[TType];

export interface AiChatEventEnvelopeBase<TType extends AiChatEventType> {
  schemaVersion: typeof AI_CHAT_EVENT_SCHEMA_VERSION;
  eventId: string;
  type: TType;
  conversationId: string;
  runId: string;
  turnId: string;
  sequence: number;
  payload: AiChatEventPayload<TType>;
  traceId?: string;
  toolCallId?: string;
  approvalId?: string;
  /** Id of the agent that produced the event (chat assistant or a delegated sub-agent). */
  agentId?: string;
  createdAt?: string;
}

export type AiChatEventEnvelope<TType extends AiChatEventType = AiChatEventType> = {
  [K in TType]: AiChatEventEnvelopeBase<K>;
}[TType];

export interface AiChatStructuredPayloadEventContext {
  conversationId: string;
  runId: string;
  turnId: string;
  firstSequence: number;
  eventIdForMessage: (message: AiChatMessageSnapshot, index: number) => string;
  traceId?: string;
  createdAt?: string;
}

export type AiChatEventValidationCode =
  | 'malformed_event'
  | 'unknown_schema_version'
  | 'unknown_event_type'
  | 'payload_too_large'
  | 'duplicate_event'
  | 'out_of_order_sequence'
  | 'mismatched_event_identity'
  | 'event_after_terminal';

export type AiChatEventValidationResult =
  | { ok: true }
  | {
      ok: false;
      code: AiChatEventValidationCode;
      message: string;
    };

export type AiChatEventPosition =
  | 'accept'
  | 'duplicate'
  | 'old'
  | 'gap'
  | 'mismatched_identity'
  | 'invalid';

const eventTypeSet = new Set<string>(AI_CHAT_EVENT_TYPES);
const terminalEventTypeSet = new Set<string>(AI_CHAT_TERMINAL_EVENT_TYPES);
const turnStatusSet = new Set<string>(AI_CHAT_TURN_STATUSES);
const terminalOutcomeCodeSet = new Set<string>(AI_CHAT_TERMINAL_OUTCOME_CODES);
const clientActionTypeSet = new Set<string>(AI_CHAT_CLIENT_ACTION_TYPES);
const clientActionRouteSet = new Set<string>(AI_CHAT_CLIENT_ACTION_ROUTES);
const clientActionViewSet = new Set<string>(AI_CHAT_CLIENT_VIEW_VALUES);
const clientActionFilterKeySet = new Set<string>(AI_CHAT_CLIENT_FILTER_KEYS);
const clientActionFilterValueSet = new Set<string>(AI_CHAT_CLIENT_FILTER_VALUES);
const encoder = new TextEncoder();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

function isOptionalNullableString(value: unknown): boolean {
  return value === undefined || value === null || typeof value === 'string';
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function hasOnlyKeys(value: Record<string, unknown>, allowedKeys: readonly string[]): boolean {
  const allowed = new Set(allowedKeys);
  return Object.keys(value).every((key) => allowed.has(key));
}

function hasRecordProperty<TProperty extends string>(
  value: Record<string, unknown>,
  property: TProperty,
): value is Record<TProperty, Record<string, unknown>> & Record<string, unknown> {
  return isRecord(value[property]);
}

function jsonByteSize(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).byteLength;
}

function isUsage(value: unknown): value is Usage {
  return (
    isRecord(value) &&
    isFiniteNumber(value.promptTokens) &&
    isFiniteNumber(value.completionTokens) &&
    isFiniteNumber(value.totalTokens) &&
    isFiniteNumber(value.costRub)
  );
}

function isTerminalOutcome(value: unknown): value is AiChatTerminalOutcome {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.code) ||
    !terminalOutcomeCodeSet.has(value.code)
  ) {
    return false;
  }
  if (!hasOnlyKeys(value, ['code', 'field', 'candidateHints'])) return false;
  if (value.field !== undefined && (!isNonEmptyString(value.field) || value.field.length > 120)) {
    return false;
  }
  if (value.candidateHints === undefined) return true;
  return (
    Array.isArray(value.candidateHints) &&
    value.candidateHints.length <= 10 &&
    value.candidateHints.every(
      (candidate) =>
        isRecord(candidate) &&
        hasOnlyKeys(candidate, ['label', 'entityType']) &&
        isNonEmptyString(candidate.label) &&
        candidate.label.length <= 200 &&
        (candidate.entityType === undefined ||
          (isNonEmptyString(candidate.entityType) && candidate.entityType.length <= 120)),
    )
  );
}

/**
 * Returns a fresh, schema-validated terminal outcome suitable for crossing a
 * persistence or replay boundary. Callers must not retain an untrusted input
 * object even after it has passed validation: this projection deliberately
 * copies only the public terminal contract.
 */
export function sanitizeAiChatTerminalOutcome(value: unknown): AiChatTerminalOutcome | undefined {
  if (!isTerminalOutcome(value)) return undefined;
  return {
    code: value.code,
    ...(value.field === undefined ? {} : { field: value.field }),
    ...(value.candidateHints === undefined
      ? {}
      : {
          candidateHints: value.candidateHints.map((candidate) => ({
            label: candidate.label,
            ...(candidate.entityType === undefined ? {} : { entityType: candidate.entityType }),
          })),
        }),
  };
}

function isClientActionFilterSpec(value: unknown): value is AiChatClientFilterSpec {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['key', 'value']) &&
    isNonEmptyString(value.key) &&
    clientActionFilterKeySet.has(value.key) &&
    isNonEmptyString(value.value) &&
    clientActionFilterValueSet.has(value.value)
  );
}

function isClientAction(value: unknown): value is AiChatClientAction {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.action) ||
    !clientActionTypeSet.has(value.action) ||
    !isNonEmptyString(value.route) ||
    !clientActionRouteSet.has(value.route)
  ) {
    return false;
  }
  if (value.action === 'navigate') {
    return hasOnlyKeys(value, ['action', 'route']);
  }
  // The route is compared literally instead of being looked up in a table: the
  // pairing is part of the contract, so a `set_filter` addressed at any other
  // section is rejected here rather than being silently reinterpreted.
  if (value.action === 'set_filter') {
    return (
      value.route === 'contacts' &&
      hasOnlyKeys(value, ['action', 'route', 'filter']) &&
      hasRecordProperty(value, 'filter') &&
      isClientActionFilterSpec(value.filter)
    );
  }
  if (value.action === 'switch_view') {
    return (
      value.route === 'deals' &&
      hasOnlyKeys(value, ['action', 'route', 'view']) &&
      isNonEmptyString(value.view) &&
      clientActionViewSet.has(value.view)
    );
  }
  return (
    hasOnlyKeys(value, ['action', 'route', 'entityId']) &&
    isNonEmptyString(value.entityId) &&
    AI_CHAT_CLIENT_ENTITY_ID_PATTERN.test(value.entityId)
  );
}

/**
 * Returns a fresh, allowlisted client action suitable for crossing the durable
 * ledger or the SSE boundary. Like the terminal-outcome projection above, it
 * copies only the public navigation contract instead of forwarding the caller's
 * object, so an unrecognized key can never ride along into an envelope.
 */
export function sanitizeAiChatClientAction(value: unknown): AiChatClientAction | undefined {
  if (!isClientAction(value)) return undefined;
  if (value.action === 'navigate') {
    return { action: 'navigate', route: value.route };
  }
  if (value.action === 'set_filter') {
    return {
      action: 'set_filter',
      route: 'contacts',
      filter: { key: value.filter.key, value: value.filter.value },
    };
  }
  if (value.action === 'switch_view') {
    return { action: 'switch_view', route: 'deals', view: value.view };
  }
  return { action: 'open_entity', route: value.route, entityId: value.entityId };
}

function isToolCall(value: unknown): value is ToolCall {
  return (
    isRecord(value) &&
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.name) &&
    isRecord(value.args)
  );
}

function isToolResult(value: unknown): value is ToolResult {
  return isRecord(value) && isNonEmptyString(value.toolCallId);
}

function isApproval(value: unknown): value is Approval {
  return (
    isRecord(value) &&
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.conversationId) &&
    isNonEmptyString(value.toolName) &&
    isRecord(value.args) &&
    isFiniteNumber(value.timeoutAt) &&
    isNonEmptyString(value.status)
  );
}

const actionPlanBulkSampleStatusSet = new Set<string>([
  'pending',
  'succeeded',
  'failed',
  'skipped',
  'stale',
]);

const actionPlanBulkAggregateFields = [
  'succeeded',
  'failed',
  'skipped',
  'stale',
  'unfinished',
] as const;

function isNonNegativeSafeInteger(value: unknown): boolean {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

/**
 * Strict runtime shape check for the optional bulk outcome block of a
 * `crm_action_plan_v1` item. Counts are non-negative safe integers, the
 * sample is capped at 20 label-only entries with statuses from the
 * coordinator outcome vocabulary. Items without the block (older v1
 * payloads) never reach this check, so their validation is unchanged.
 */
function isActionPlanBulkOutcome(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (!isNonNegativeSafeInteger(value.count)) return false;
  if (!hasRecordProperty(value, 'aggregate')) return false;
  if (
    !actionPlanBulkAggregateFields.every((field) =>
      isNonNegativeSafeInteger(value.aggregate[field]),
    )
  ) {
    return false;
  }
  if (!Array.isArray(value.sample) || value.sample.length > 20) return false;
  return value.sample.every(
    (entry) =>
      isRecord(entry) &&
      isNonEmptyString(entry.label) &&
      entry.label.length <= 200 &&
      isNonEmptyString(entry.status) &&
      actionPlanBulkSampleStatusSet.has(entry.status),
  );
}

function isStructuredMessagePayload(value: unknown): value is StructuredMessagePayload {
  if (!isRecord(value) || !isNonEmptyString(value.type)) {
    return false;
  }

  switch (value.type) {
    case 'compound_clarification_v1': {
      const binding = value.binding;
      return (
        value.version === 1 &&
        isRecord(binding) &&
        ['draftId', 'revisionId'].every(
          (key) => isNonEmptyString(binding[key]) && String(binding[key]).length <= 200,
        ) &&
        ['draftVersion', 'workingStateVersion'].every(
          (key) => Number.isSafeInteger(binding[key]) && Number(binding[key]) >= 0,
        ) &&
        ['contentHash', 'sourceHash'].every((key) =>
          /^sha256-jcs-v1:[a-f0-9]{64}$/.test(String(binding[key])),
        ) &&
        Object.keys(binding).every((key) =>
          [
            'draftId',
            'revisionId',
            'draftVersion',
            'workingStateVersion',
            'contentHash',
            'sourceHash',
          ].includes(key),
        ) &&
        Array.isArray(value.candidateLabels) &&
        value.candidateLabels.length >= 2 &&
        value.candidateLabels.length <= AI_CHAT_AMBIGUITY_CANDIDATE_LIMIT &&
        value.candidateLabels.every((label) => isNonEmptyString(label) && label.length <= 300) &&
        Object.keys(value).every((key) =>
          ['type', 'version', 'binding', 'candidateLabels'].includes(key),
        )
      );
    }
    case 'pending_batch_correction_v1':
      return (
        value.version === 1 &&
        ['planId', 'draftId', 'revisionId'].every(
          (key) => isNonEmptyString(value[key]) && (value[key] as string).length <= 200,
        ) &&
        ['planVersion', 'draftVersion', 'workingStateVersion'].every(
          (key) => Number.isSafeInteger(value[key]) && (value[key] as number) >= 0,
        ) &&
        Object.keys(value).every((key) =>
          [
            'type',
            'version',
            'planId',
            'planVersion',
            'draftId',
            'revisionId',
            'draftVersion',
            'workingStateVersion',
          ].includes(key),
        )
      );
    case 'pending_task_correction_v1': {
      const binding = value.binding;
      return (
        value.version === 1 &&
        (value.mode === 'simple' || value.mode === 'compound') &&
        (value.timeZone === undefined ||
          (isNonEmptyString(value.timeZone) && value.timeZone.length <= 100)) &&
        isRecord(binding) &&
        ['draftId', 'revisionId'].every(
          (key) => isNonEmptyString(binding[key]) && (binding[key] as string).length <= 200,
        ) &&
        ['draftVersion', 'workingStateVersion'].every(
          (key) => Number.isSafeInteger(binding[key]) && (binding[key] as number) >= 0,
        ) &&
        typeof binding.contentHash === 'string' &&
        /^sha256-jcs-v1:[a-f0-9]{64}$/u.test(binding.contentHash) &&
        Object.keys(binding).every((key) =>
          ['draftId', 'revisionId', 'draftVersion', 'workingStateVersion', 'contentHash'].includes(
            key,
          ),
        ) &&
        Object.keys(value).every((key) =>
          ['type', 'version', 'mode', 'timeZone', 'binding'].includes(key),
        )
      );
    }
    case 'deal':
      return hasRecordProperty(value, 'deal') && isNonEmptyString(value.deal.id);
    case 'deal_summary':
      return hasRecordProperty(value, 'summary') && hasRecordProperty(value.summary, 'deal');
    case 'deal_cockpit':
      return (
        hasRecordProperty(value, 'cockpit') &&
        hasRecordProperty(value.cockpit, 'deal') &&
        isNonEmptyString(value.cockpit.deal.id) &&
        isNonEmptyString(value.cockpit.deal.title) &&
        isNonEmptyString(value.cockpit.deal.stage) &&
        isFiniteNumber(value.cockpit.deal.amount) &&
        isNonEmptyString(value.cockpit.deal.currency) &&
        hasRecordProperty(value.cockpit, 'health') &&
        isFiniteNumber(value.cockpit.health.score) &&
        ['low', 'medium', 'high'].includes(String(value.cockpit.health.level)) &&
        isFiniteNumber(value.cockpit.health.signalCount) &&
        Array.isArray(value.cockpit.signals) &&
        Array.isArray(value.cockpit.tasks) &&
        Array.isArray(value.cockpit.recentActivities) &&
        hasRecordProperty(value.cockpit, 'metrics') &&
        // B2: optional closing-qualification checklist — present only when the
        // deal has a stored block; malformed content rejects the payload
        // instead of rendering a broken checklist.
        (value.cockpit.qualification === undefined ||
          value.cockpit.qualification === null ||
          (isRecord(value.cockpit.qualification) &&
            Array.isArray(value.cockpit.qualification.items) &&
            value.cockpit.qualification.items.length <= 10 &&
            value.cockpit.qualification.items.every(
              (item) =>
                isRecord(item) &&
                isNonEmptyString(item.field) &&
                item.field.length <= 64 &&
                ['unknown', 'confirmed', 'missing'].includes(String(item.status)) &&
                (item.value === undefined ||
                  item.value === null ||
                  (typeof item.value === 'string' && item.value.length <= 500)),
            )))
      );
    case 'deal_action_plan':
      return (
        hasRecordProperty(value, 'plan') &&
        hasRecordProperty(value.plan, 'deal') &&
        isNonEmptyString(value.plan.deal.id) &&
        isNonEmptyString(value.plan.deal.title) &&
        isNonEmptyString(value.plan.deal.stage) &&
        Array.isArray(value.plan.actions) &&
        value.plan.actions.length > 0 &&
        value.plan.actions.every(
          (action) =>
            isRecord(action) &&
            isNonEmptyString(action.title) &&
            ['low', 'medium', 'high', 'urgent'].includes(String(action.priority)) &&
            isOptionalNullableString(action.description) &&
            isOptionalNullableString(action.dueAt) &&
            isOptionalNullableString(action.rationale),
        ) &&
        hasRecordProperty(value.plan, 'context') &&
        isFiniteNumber(value.plan.context.openTaskCount) &&
        isFiniteNumber(value.plan.context.recentNoteCount)
      );
    case 'deal_risk':
      return (
        hasRecordProperty(value, 'risk') &&
        hasRecordProperty(value.risk, 'deal') &&
        isNonEmptyString(value.risk.deal.id) &&
        isNonEmptyString(value.risk.deal.title) &&
        isNonEmptyString(value.risk.deal.stage) &&
        ['low', 'medium', 'high'].includes(String(value.risk.level)) &&
        Array.isArray(value.risk.signals) &&
        value.risk.signals.every(
          (signal) =>
            isRecord(signal) &&
            isNonEmptyString(signal.code) &&
            ['medium', 'high'].includes(String(signal.severity)) &&
            isNonEmptyString(signal.message),
        ) &&
        hasRecordProperty(value.risk, 'recommendation') &&
        isNonEmptyString(value.risk.recommendation.title) &&
        isNonEmptyString(value.risk.recommendation.description) &&
        ['email', 'call', 'none'].includes(String(value.risk.recommendation.channel))
      );
    case 'manager_briefing':
      return (
        hasRecordProperty(value, 'briefing') &&
        Array.isArray(value.briefing.hotLeads) &&
        Array.isArray(value.briefing.overdueTasks) &&
        Array.isArray(value.briefing.riskDeals) &&
        Array.isArray(value.briefing.priorities)
      );
    case 'deal_call_preparation':
      return (
        hasRecordProperty(value, 'preparation') && hasRecordProperty(value.preparation, 'deal')
      );
    case 'meeting_outcome':
      return (
        hasRecordProperty(value, 'meeting') &&
        hasRecordProperty(value.meeting, 'deal') &&
        isNonEmptyString(value.meeting.deal.id) &&
        ['ready', 'no_note', 'too_long', 'analysis_unavailable'].includes(
          String(value.meeting.status),
        )
      );
    case 'sales_health': {
      if (
        !hasRecordProperty(value, 'overview') ||
        !isNonEmptyString(
          (value.overview as unknown as Record<string, unknown> | undefined)?.['mode'],
        ) ||
        String((value.overview as Record<string, unknown>)['mode']) !== 'sales_health' ||
        !Array.isArray(value.overview.totalsByCurrency) ||
        !Array.isArray(value.overview.stages) ||
        !Array.isArray(value.overview.topDeals)
      ) {
        return false;
      }
      // Counters must be finite non-negative integers that add up: otherwise a
      // broken projection would render as an empty-but-valid funnel.
      const overviewRecord = value.overview as unknown as Record<string, unknown>;
      const isNonNegativeInteger = (raw: unknown): raw is number =>
        typeof raw === 'number' && Number.isInteger(raw) && raw >= 0;
      const activeDealCount = overviewRecord['activeDealCount'];
      const healthyCount = overviewRecord['healthyCount'];
      const attentionCount = overviewRecord['attentionCount'];
      const criticalCount = overviewRecord['criticalCount'];
      if (
        !isNonNegativeInteger(activeDealCount) ||
        !isNonNegativeInteger(healthyCount) ||
        !isNonNegativeInteger(attentionCount) ||
        !isNonNegativeInteger(criticalCount)
      ) {
        return false;
      }
      if (healthyCount + attentionCount + criticalCount !== activeDealCount) return false;
      return value.overview.stages.every(
        (stageRow: unknown) =>
          isRecord(stageRow) &&
          isNonEmptyString(stageRow['stage']) &&
          Array.isArray(stageRow['totalsByCurrency']),
      );
    }
    case 'contact':
      return hasRecordProperty(value, 'contact') && isNonEmptyString(value.contact.id);
    case 'email_draft':
      return hasRecordProperty(value, 'draft') && isNonEmptyString(value.draft.subject);
    case 'memory':
      return hasRecordProperty(value, 'memory') && isNonEmptyString(value.memory.valueText);
    case 'scenario':
      return hasRecordProperty(value, 'scenario') && isNonEmptyString(value.scenario.name);
    case 'add_deal_note_confirmation':
      return (
        hasRecordProperty(value, 'note') &&
        isNonEmptyString(value.note.dealId) &&
        hasRecordProperty(value, 'action') &&
        value.action.type === 'confirm_add_deal_note' &&
        hasRecordProperty(value.action, 'payload') &&
        (value.action.payload.approvalId === undefined ||
          isNonEmptyString(value.action.payload.approvalId))
      );
    case 'view':
      return (
        hasRecordProperty(value, 'view') && isRecord(value.view.spec) && isRecord(value.view.result)
      );
    case 'artifact':
      return (
        hasRecordProperty(value, 'artifact') &&
        isNonEmptyString(value.artifact.id) &&
        isNonEmptyString(value.artifact.title) &&
        isNonEmptyString(value.artifact.content)
      );
    case 'crm_list_v1':
      return (
        hasRecordProperty(value, 'list') &&
        isNonEmptyString(value.list.entityType) &&
        Number.isInteger(value.list.total) &&
        (value.list.total as number) >= 0 &&
        typeof value.list.hasMore === 'boolean' &&
        (value.list.nextCursor === null || isNonEmptyString(value.list.nextCursor)) &&
        Array.isArray(value.list.appliedFilters) &&
        value.list.appliedFilters.every(
          (filter) =>
            isRecord(filter) &&
            isNonEmptyString(filter.field) &&
            isNonEmptyString(filter.operator) &&
            (filter.value === null ||
              typeof filter.value === 'string' ||
              typeof filter.value === 'number' ||
              typeof filter.value === 'boolean'),
        ) &&
        Array.isArray(value.list.rows) &&
        value.list.rows.length <= 50 &&
        value.list.rows.every(
          (row) =>
            isRecord(row) &&
            isNonEmptyString(row.id) &&
            isNonEmptyString(row.label) &&
            (row.url === null || isNonEmptyString(row.url)) &&
            Array.isArray(row.fields) &&
            row.fields.every(
              (field) =>
                isRecord(field) &&
                isNonEmptyString(field.key) &&
                (field.referenceId === undefined || isNonEmptyString(field.referenceId)) &&
                isNonEmptyString(field.label) &&
                (field.value === null ||
                  typeof field.value === 'string' ||
                  typeof field.value === 'number' ||
                  typeof field.value === 'boolean'),
            ),
        )
      );
    case 'crm_action_plan_v1':
      return (
        hasRecordProperty(value, 'plan') &&
        (value.plan.remainingApproval === undefined ||
          (isRecord(value.plan.remainingApproval) &&
            Number.isSafeInteger(value.plan.remainingApproval.count) &&
            Number(value.plan.remainingApproval.count) >= 2 &&
            Number(value.plan.remainingApproval.count) <= 10 &&
            typeof value.plan.remainingApproval.token === 'string' &&
            /^[a-f0-9]{64}$/.test(value.plan.remainingApproval.token))) &&
        (value.plan.groupExecution === undefined ||
          (isRecord(value.plan.groupExecution) &&
            ['executing', 'complete'].includes(String(value.plan.groupExecution.state)) &&
            Array.isArray(value.plan.groupExecution.results) &&
            value.plan.groupExecution.results.length <= 10 &&
            value.plan.groupExecution.results.every(
              (row) =>
                isRecord(row) &&
                Number.isSafeInteger(row.ordinal) &&
                Number(row.ordinal) >= 0 &&
                Number(row.ordinal) <= 9 &&
                typeof row.label === 'string' &&
                row.label.length <= 300 &&
                ['pending', 'succeeded', 'failed'].includes(String(row.status)) &&
                (row.error === undefined || typeof row.error === 'string'),
            ))) &&
        isNonEmptyString(value.plan.id) &&
        Array.isArray(value.plan.items) &&
        value.plan.items.length > 0 &&
        value.plan.items.every(
          (item) =>
            isRecord(item) &&
            isNonEmptyString(item.id) &&
            isFiniteNumber(item.ordinal) &&
            isNonEmptyString(item.action) &&
            hasRecordProperty(item, 'entity') &&
            isNonEmptyString(item.entity.type) &&
            isNonEmptyString(item.entity.label) &&
            Array.isArray(item.fields) &&
            isNonEmptyString(item.status) &&
            (item.bulk === undefined || isActionPlanBulkOutcome(item.bulk)) &&
            (item.retryExecutionId === undefined || isNonEmptyString(item.retryExecutionId)),
        ) &&
        // PR 5 file→plan provenance (plan level, optional non-empty strings;
        // symmetric with the kit payload contract).
        (value.plan.sourceAttachmentId === undefined ||
          isNonEmptyString(value.plan.sourceAttachmentId)) &&
        (value.plan.sourceContentHash === undefined ||
          isNonEmptyString(value.plan.sourceContentHash))
      );
    case 'chat_file_attachment_v1':
      return (
        isNonEmptyString(value.attachmentId) &&
        isNonEmptyString(value.fileName) &&
        isNonEmptyString(value.format) &&
        isNonEmptyString(value.contentHash)
      );
    case 'model_upgrade_offer':
      return (
        isNonEmptyString(value.currentModel) &&
        isNonEmptyString(value.fallbackModel) &&
        ['L0', 'L1', 'L2', 'L3', 'L4', 'L5', 'L6'].includes(String(value.requestedLevel)) &&
        (value.currentCertifiedThrough === null ||
          ['L0', 'L1', 'L2', 'L3', 'L4', 'L5', 'L6'].includes(
            String(value.currentCertifiedThrough),
          )) &&
        value.action === 'request_model_upgrade' &&
        isNonEmptyString(value.title) &&
        isNonEmptyString(value.message) &&
        isNonEmptyString(value.confirmLabel)
      );
    case 'crm_touch_strategy_v1':
      return (
        isNonEmptyString(value.dealId) &&
        isNonEmptyString(value.diagnosis) &&
        Array.isArray(value.steps) &&
        value.steps.length >= 1 &&
        value.steps.length <= 5 &&
        value.steps.every(
          (step) =>
            isRecord(step) &&
            Number.isInteger(step.dayOffset) &&
            (step.dayOffset as number) >= 0 &&
            (step.dayOffset as number) <= 14 &&
            isNonEmptyString(step.channel) &&
            ['email', 'call', 'meeting', 'telegram'].includes(String(step.channel)) &&
            isNonEmptyString(step.goal) &&
            (step.draft === undefined ||
              step.draft === null ||
              (typeof step.draft === 'string' && step.draft.length <= 2000)),
        ) &&
        Array.isArray(value.evidenceSignalCodes) &&
        value.evidenceSignalCodes.every((code) => isNonEmptyString(code)) &&
        isNonEmptyString(value.sourcePreset) &&
        ['proposal_silent', 'stale_silence', 'discovery', 'no_next_step'].includes(
          String(value.sourcePreset),
        ) &&
        // B3: optional approval linkage strings — present only when non-empty,
        // symmetric with the add_deal_note_confirmation approval sync pattern.
        (value.approvalId === undefined || isNonEmptyString(value.approvalId)) &&
        (value.approvalStatus === undefined || isNonEmptyString(value.approvalStatus)) &&
        (value.decidedAt === undefined || isNonEmptyString(value.decidedAt)) &&
        (value.executionStatus === undefined || isNonEmptyString(value.executionStatus)) &&
        // B4: read-time staleness hint — optional boolean only.
        (value.historyAdvancedSince === undefined ||
          typeof value.historyAdvancedSince === 'boolean')
      );
    case 'crm_qualification_proposal_v1':
      return (
        isNonEmptyString(value.dealId) &&
        Array.isArray(value.items) &&
        value.items.length >= 1 &&
        value.items.length <= 5 &&
        value.items.every(
          (item) =>
            isRecord(item) &&
            isNonEmptyString(item.field) &&
            ['need', 'budget', 'decisionMaker', 'decisionDeadline', 'selectionCriteria'].includes(
              String(item.field),
            ) &&
            isNonEmptyString(item.status) &&
            ['unknown', 'confirmed', 'missing'].includes(String(item.status)) &&
            (item.value === undefined ||
              item.value === null ||
              (typeof item.value === 'string' && item.value.length <= 300)) &&
            (item.evidence === undefined ||
              item.evidence === null ||
              (typeof item.evidence === 'string' && item.evidence.length <= 500)),
        ) &&
        // B2: optional approval linkage strings — present only when non-empty,
        // symmetric with the touch-strategy approval sync pattern.
        (value.approvalId === undefined || isNonEmptyString(value.approvalId)) &&
        (value.approvalStatus === undefined || isNonEmptyString(value.approvalStatus)) &&
        (value.decidedAt === undefined || isNonEmptyString(value.decidedAt)) &&
        (value.executionStatus === undefined || isNonEmptyString(value.executionStatus))
      );
    default:
      return false;
  }
}

function isMessageSnapshot(value: unknown): value is AiChatMessageSnapshot {
  return (
    isRecord(value) &&
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.role) &&
    ['user', 'assistant', 'tool', 'system'].includes(value.role) &&
    typeof value.content === 'string' &&
    (value.structuredPayload === undefined ||
      value.structuredPayload === null ||
      isStructuredMessagePayload(value.structuredPayload)) &&
    (value.toolCalls === undefined ||
      value.toolCalls === null ||
      (Array.isArray(value.toolCalls) && value.toolCalls.every(isToolCall))) &&
    (value.toolCallId === undefined ||
      value.toolCallId === null ||
      typeof value.toolCallId === 'string') &&
    (value.createdAt === undefined ||
      typeof value.createdAt === 'string' ||
      value.createdAt instanceof Date)
  );
}

function isValidPayloadForType(type: AiChatEventType, payload: Record<string, unknown>): boolean {
  switch (type) {
    case 'assistant_delta':
      return typeof payload.content === 'string';
    case 'reasoning_delta':
      return typeof payload.content === 'string';
    case 'tool_call':
      return isToolCall(payload.call);
    case 'tool_result':
      return isToolResult(payload.result);
    case 'approval_required':
      return isApproval(payload.approval);
    case 'usage':
      return isUsage(payload.usage);
    case 'structured_payload':
      return isNonEmptyString(payload.messageId) && isStructuredMessagePayload(payload.payload);
    case 'client_action':
      return isClientAction(payload.action);
    case 'turn_status':
      return false;
    case 'done':
      return (
        payload.state === 'completed' &&
        (payload.messages === undefined ||
          (Array.isArray(payload.messages) && payload.messages.every(isMessageSnapshot))) &&
        (payload.usage === undefined || isUsage(payload.usage)) &&
        isOptionalString(payload.traceId) &&
        (payload.terminalOutcome === undefined || isTerminalOutcome(payload.terminalOutcome))
      );
    case 'error':
      return (
        payload.state === 'failed' &&
        hasRecordProperty(payload, 'error') &&
        isNonEmptyString(payload.error.message) &&
        isOptionalString(payload.error.code) &&
        (payload.error.terminalOutcome === undefined ||
          isTerminalOutcome(payload.error.terminalOutcome))
      );
  }
}

function isTerminalTurnStatus(status: AiChatTurnStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

function isValidTurnStatusPayload(
  payload: Record<string, unknown>,
  envelope: Record<string, unknown>,
): boolean {
  if (
    !isNonEmptyString(payload.status) ||
    !turnStatusSet.has(payload.status) ||
    !isRecord(payload.cursor) ||
    payload.cursor.eventId !== envelope.eventId ||
    payload.cursor.runId !== envelope.runId ||
    payload.cursor.turnId !== envelope.turnId ||
    payload.cursor.sequence !== envelope.sequence
  ) {
    return false;
  }

  const status = payload.status as AiChatTurnStatus;
  // `failed` and `cancelled` share one error shape. `cancelled` carries it only
  // for a non-user stop (deadline expiry); without an error it stays a plain
  // user cancel. Every other status must not carry an error at all.
  if (status === 'failed' || status === 'cancelled') {
    if (status === 'cancelled' && payload.error === undefined) return true;
    return (
      hasRecordProperty(payload, 'error') &&
      hasOnlyKeys(payload.error, ['message', 'code', 'retryable']) &&
      isNonEmptyString(payload.error.message) &&
      isOptionalString(payload.error.code) &&
      (payload.error.retryable === undefined || typeof payload.error.retryable === 'boolean')
    );
  }

  return payload.error === undefined;
}

export function isAiChatTerminalEvent(type: string): boolean {
  return terminalEventTypeSet.has(type);
}

export function isAiChatTerminalEnvelope(event: AiChatEventEnvelope): boolean {
  return (
    event.type === 'done' ||
    event.type === 'error' ||
    (event.type === 'turn_status' && isTerminalTurnStatus(event.payload.status))
  );
}

export function classifyAiChatEventPosition(
  event: AiChatEventCursor,
  lastAccepted?: AiChatEventCursor,
): AiChatEventPosition {
  if (
    !isValidAiChatEventCursor(event) ||
    (lastAccepted && !isValidAiChatEventCursor(lastAccepted))
  ) {
    return 'invalid';
  }
  if (lastAccepted === undefined) {
    return 'accept';
  }
  if (event.runId !== lastAccepted.runId || event.turnId !== lastAccepted.turnId) {
    return 'mismatched_identity';
  }
  if (event.eventId === lastAccepted.eventId) {
    return 'duplicate';
  }
  if (event.sequence <= lastAccepted.sequence) {
    return 'old';
  }
  if (event.sequence > lastAccepted.sequence + 1) {
    return 'gap';
  }
  return 'accept';
}

function isValidAiChatEventCursor(cursor: AiChatEventCursor): boolean {
  return (
    isNonEmptyString(cursor.eventId) &&
    isNonEmptyString(cursor.runId) &&
    isNonEmptyString(cursor.turnId) &&
    Number.isSafeInteger(cursor.sequence) &&
    cursor.sequence >= 0
  );
}

export function buildAiChatEvent<TType extends AiChatEventType>(
  input: Omit<AiChatEventEnvelopeBase<TType>, 'schemaVersion'>,
): AiChatEventEnvelope<TType> {
  return {
    schemaVersion: AI_CHAT_EVENT_SCHEMA_VERSION,
    ...input,
  } as AiChatEventEnvelope<TType>;
}

export function extractCanonicalStructuredPayloadEvents(
  messages: readonly AiChatMessageSnapshot[],
  context: AiChatStructuredPayloadEventContext,
): Array<AiChatEventEnvelope<'structured_payload'>> {
  return messages.flatMap((message, index) => {
    if (message.structuredPayload === undefined || message.structuredPayload === null) {
      return [];
    }

    return [
      buildAiChatEvent({
        eventId: context.eventIdForMessage(message, index),
        conversationId: context.conversationId,
        runId: context.runId,
        turnId: context.turnId,
        sequence: context.firstSequence + index,
        type: 'structured_payload',
        payload: {
          messageId: message.id,
          payload: message.structuredPayload,
        },
        ...(context.traceId === undefined ? {} : { traceId: context.traceId }),
        ...(context.createdAt === undefined ? {} : { createdAt: context.createdAt }),
      }),
    ];
  });
}

export class AiChatEventReplayGuard {
  readonly #seenEventIds = new Set<string>();
  readonly #lastSequenceByRunId = new Map<string, number>();
  readonly #identityByRunId = new Map<string, { conversationId: string; turnId: string }>();
  readonly #terminalRunIds = new Set<string>();

  validate(event: unknown): AiChatEventValidationResult {
    const shape = validateAiChatEventShape(event);
    if (!shape.ok) {
      return shape;
    }

    const envelope = event as AiChatEventEnvelope;
    if (this.#seenEventIds.has(envelope.eventId)) {
      return {
        ok: false,
        code: 'duplicate_event',
        message: `Duplicate ai-chat event id: ${envelope.eventId}`,
      };
    }

    const acceptedIdentity = this.#identityByRunId.get(envelope.runId);
    if (
      acceptedIdentity !== undefined &&
      (acceptedIdentity.conversationId !== envelope.conversationId ||
        acceptedIdentity.turnId !== envelope.turnId)
    ) {
      return {
        ok: false,
        code: 'mismatched_event_identity',
        message: `Run ${envelope.runId} does not match its accepted conversation and turn`,
      };
    }

    if (this.#terminalRunIds.has(envelope.runId)) {
      return {
        ok: false,
        code: 'event_after_terminal',
        message: `Run ${envelope.runId} already reached a terminal event`,
      };
    }

    const lastSequence = this.#lastSequenceByRunId.get(envelope.runId);
    if (lastSequence !== undefined && envelope.sequence <= lastSequence) {
      return {
        ok: false,
        code: 'out_of_order_sequence',
        message: `Event sequence ${envelope.sequence} is not after ${lastSequence}`,
      };
    }

    this.#seenEventIds.add(envelope.eventId);
    this.#lastSequenceByRunId.set(envelope.runId, envelope.sequence);
    this.#identityByRunId.set(envelope.runId, {
      conversationId: envelope.conversationId,
      turnId: envelope.turnId,
    });
    if (isAiChatTerminalEnvelope(envelope)) {
      this.#terminalRunIds.add(envelope.runId);
    }

    return { ok: true };
  }
}

export function createAiChatEventReplayGuard(): AiChatEventReplayGuard {
  return new AiChatEventReplayGuard();
}

export function validateAiChatEventShape(event: unknown): AiChatEventValidationResult {
  if (!isRecord(event)) {
    return {
      ok: false,
      code: 'malformed_event',
      message: 'AI chat event must be an object',
    };
  }

  if (event.schemaVersion !== AI_CHAT_EVENT_SCHEMA_VERSION) {
    return {
      ok: false,
      code: 'unknown_schema_version',
      message: `Unsupported AI chat event schemaVersion: ${String(event.schemaVersion)}`,
    };
  }

  if (
    !isNonEmptyString(event.eventId) ||
    !isNonEmptyString(event.conversationId) ||
    !isNonEmptyString(event.runId) ||
    !isNonEmptyString(event.turnId) ||
    !Number.isSafeInteger(event.sequence) ||
    (event.sequence as number) < 0 ||
    !isRecord(event.payload) ||
    !isOptionalString(event.traceId) ||
    !isOptionalString(event.toolCallId) ||
    !isOptionalString(event.approvalId) ||
    !isOptionalString(event.createdAt)
  ) {
    return {
      ok: false,
      code: 'malformed_event',
      message: 'AI chat event is missing required envelope fields',
    };
  }

  if (!isNonEmptyString(event.type) || !eventTypeSet.has(event.type)) {
    return {
      ok: false,
      code: 'unknown_event_type',
      message: `Unsupported AI chat event type: ${String(event.type)}`,
    };
  }

  const eventType = event.type as AiChatEventType;
  const validPayload =
    eventType === 'turn_status'
      ? isValidTurnStatusPayload(event.payload, event)
      : isValidPayloadForType(eventType, event.payload);
  if (!validPayload) {
    return {
      ok: false,
      code: 'malformed_event',
      message: `AI chat event payload does not match type ${eventType}`,
    };
  }

  if (jsonByteSize(event.payload) > AI_CHAT_EVENT_MAX_PAYLOAD_BYTES) {
    return {
      ok: false,
      code: 'payload_too_large',
      message: `AI chat event payload exceeds ${AI_CHAT_EVENT_MAX_PAYLOAD_BYTES} bytes`,
    };
  }

  return { ok: true };
}
